import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

const LOCATION_TYPES = [
  'Clinic','Hospital','Pharmacy','Dental','Optical','Wellness','Gym','Spa',
  'Mental Health','Physiotherapy','Laboratory','Urgent Care','Telehealth','Other',
];

function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: '.63rem', color: '#6F8FA7', marginBottom: 3, fontWeight: 500 }}>
        {label}
        {hint && <span style={{ marginLeft: 6, fontSize: '.58rem', color: '#9eb8c8', fontWeight: 400 }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

const inp = (val, onChange, placeholder, type) => (
  <input
    type={type || 'text'}
    value={val || ''}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder || ''}
    style={{ fontSize: '.72rem', border: '1px solid #d8eaf4', borderRadius: 7, padding: '7px 10px', color: '#1a3040', outline: 'none', fontFamily: F, width: '100%', boxSizing: 'border-box' }}
  />
);

export default function EditLocationModal({ record, onClose, onSaved }) {
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [deleting, setDel]    = useState(false);
  const [confirmDel, setConf] = useState(false);
  const [toast, setToast]     = useState(null);
  const [dirty, setDirty]     = useState(false);

  useEffect(() => {
    if (record) {
      setForm({ ...record });
      setDirty(false);
      setConf(false);
    }
  }, [record]);

  function set(key, val) {
    setForm(f => ({ ...f, [key]: val }));
    setDirty(true);
  }

  function showToast(msg, type) {
    setToast({ msg, type: type || 'ok' });
    setTimeout(() => setToast(null), 3000);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const { error } = await supabase.rpc('admin_update_location', {
        p_id:              String(form.id),
        p_name:            form.name            || null,
        p_address:         form.address         || null,
        p_phone:           form.phone           || null,
        p_website:         form.website         || null,
        p_booking_url:     form.booking_url     || null,
        p_specialties_url: form.specialties_url || null,
        p_latitude:        form.latitude        ? parseFloat(form.latitude)  : null,
        p_longitude:       form.longitude       ? parseFloat(form.longitude) : null,
        p_country:         form.country         || null,
        p_city:            form.city            || null,
        p_type:            form.type            || null,
        p_tags:            form.tags            || null,
        p_notes:           form.notes           || null,
        p_last_verified:   form.last_verified   || null,
      });
      if (error) throw error;
      showToast('Saved successfully');
      setDirty(false);
      try {
        await supabase.rpc('admin_log_action', {
          p_actor: 'admin', p_actor_email: '',
          p_action_type: 'location_edited',
          p_subject: form.name || form.id,
          p_detail: 'Fields updated via Location Editor',
        });
      } catch (_e) {}
      setTimeout(() => { onSaved && onSaved(form); }, 400);
    } catch (err) {
      showToast('Save failed: ' + (err.message || 'unknown error'), 'warn');
    }
    setSaving(false);
  }

  async function handleDelete() {
    setDel(true);
    try {
      const { error } = await supabase.rpc('admin_delete_location', { p_id: String(form.id) });
      if (error) throw error;
      showToast('Record deleted');
      try {
        await supabase.rpc('admin_log_action', {
          p_actor: 'admin', p_actor_email: '',
          p_action_type: 'location_deleted',
          p_subject: form.name || form.id,
          p_detail: 'Deleted via Location Editor',
        });
      } catch (_e) {}
      setTimeout(() => { onSaved && onSaved(null); onClose && onClose(); }, 600);
    } catch (err) {
      showToast('Delete failed: ' + (err.message || 'unknown error'), 'warn');
      setConf(false);
    }
    setDel(false);
  }

  if (!record) return null;

  const isPremium = form.phone && form.website && form.booking_url && form.specialties_url;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 8000, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', fontFamily: F }}>
      {/* Scrim */}
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(10,22,32,0.45)' }} />

      {/* Drawer */}
      <div style={{ position: 'relative', zIndex: 1, width: 480, maxWidth: '100vw', height: '100vh', background: '#fff', boxShadow: '-4px 0 32px rgba(0,0,0,0.14)', display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #edf6fb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '.72rem', color: '#9eb8c8', marginBottom: 2 }}>ID {form.id}</div>
            <div style={{ fontSize: '.9rem', fontWeight: 600, color: '#1a3040', lineHeight: 1.3 }}>{form.name || <em style={{ color: '#e05c5c' }}>No name</em>}</div>
            <div style={{ fontSize: '.65rem', color: '#6F8FA7', marginTop: 2 }}>{form.country}{form.city ? ' · ' + form.city : ''}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {isPremium && <span style={{ fontSize: '.6rem', padding: '2px 8px', borderRadius: 8, background: '#9F7E4F22', color: '#9F7E4F', fontWeight: 700 }}>PREMIUM</span>}
            <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: '50%', background: '#f5fafc', border: 'none', cursor: 'pointer', fontSize: 14, color: '#6F8FA7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          </div>
        </div>

        {/* Body — scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {/* Identity */}
          <div style={{ fontSize: '.6rem', fontWeight: 700, color: '#9eb8c8', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 10 }}>Identity</div>
          <Field label="Name">
            {inp(form.name, v => set('name', v), 'Location name')}
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Country">
              {inp(form.country, v => set('country', v), 'e.g. Japan')}
            </Field>
            <Field label="City">
              {inp(form.city, v => set('city', v), 'e.g. Tokyo')}
            </Field>
          </div>
          <Field label="Address">
            <textarea value={form.address || ''} onChange={e => set('address', e.target.value)} placeholder="Full address" rows={2}
              style={{ fontSize: '.72rem', border: '1px solid #d8eaf4', borderRadius: 7, padding: '7px 10px', color: '#1a3040', outline: 'none', fontFamily: F, width: '100%', resize: 'vertical', boxSizing: 'border-box' }} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Type">
              <select value={form.type || ''} onChange={e => set('type', e.target.value)}
                style={{ fontSize: '.72rem', border: '1px solid #d8eaf4', borderRadius: 7, padding: '7px 10px', color: '#1a3040', fontFamily: F, width: '100%' }}>
                <option value="">— select —</option>
                {LOCATION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Tags" hint="comma-separated">
              {inp(form.tags, v => set('tags', v), 'e.g. english, 24h')}
            </Field>
          </div>

          {/* Contact */}
          <div style={{ fontSize: '.6rem', fontWeight: 700, color: '#9eb8c8', textTransform: 'uppercase', letterSpacing: '.08em', margin: '16px 0 10px' }}>Contact</div>
          <Field label="Phone">
            {inp(form.phone, v => set('phone', v), '+66 2 123 4567')}
          </Field>
          <Field label="Website">
            {inp(form.website, v => set('website', v), 'https://')}
          </Field>

          {/* Premium fields */}
          <div style={{ fontSize: '.6rem', fontWeight: 700, color: '#9F7E4F', textTransform: 'uppercase', letterSpacing: '.08em', margin: '16px 0 10px' }}>Premium fields</div>
          <Field label="Booking URL">
            {inp(form.booking_url, v => set('booking_url', v), 'https://book.example.com')}
          </Field>
          <Field label="Specialties URL">
            {inp(form.specialties_url, v => set('specialties_url', v), 'https://example.com/specialties')}
          </Field>

          {/* Geo */}
          <div style={{ fontSize: '.6rem', fontWeight: 700, color: '#e05c5c', textTransform: 'uppercase', letterSpacing: '.08em', margin: '16px 0 10px' }}>GPS coordinates</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Latitude">
              {inp(form.latitude, v => set('latitude', v), '35.6762', 'number')}
            </Field>
            <Field label="Longitude">
              {inp(form.longitude, v => set('longitude', v), '139.6503', 'number')}
            </Field>
          </div>
          {form.latitude && form.longitude && (
            <a href={`https://www.google.com/maps?q=${form.latitude},${form.longitude}`} target="_blank" rel="noreferrer"
              style={{ fontSize: '.63rem', color: '#6F8FA7', textDecoration: 'underline', display: 'inline-block', marginBottom: 8 }}>
              View on Google Maps ↗
            </a>
          )}

          {/* Admin */}
          <div style={{ fontSize: '.6rem', fontWeight: 700, color: '#9eb8c8', textTransform: 'uppercase', letterSpacing: '.08em', margin: '16px 0 10px' }}>Admin</div>
          <Field label="Last verified" hint="YYYY-MM-DD">
            {inp(form.last_verified, v => set('last_verified', v), new Date().toISOString().split('T')[0])}
          </Field>
          <Field label="Notes">
            <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Internal notes…" rows={3}
              style={{ fontSize: '.72rem', border: '1px solid #d8eaf4', borderRadius: 7, padding: '7px 10px', color: '#1a3040', outline: 'none', fontFamily: F, width: '100%', resize: 'vertical', boxSizing: 'border-box' }} />
          </Field>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #edf6fb', flexShrink: 0, background: '#fff' }}>
          {toast && (
            <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 7, background: toast.type === 'warn' ? '#fef0ef' : '#f0fbf5', color: toast.type === 'warn' ? '#8b1c1c' : '#1b5e20', fontSize: '.72rem', fontWeight: 500 }}>
              {toast.msg}
            </div>
          )}

          {confirmDel ? (
            <div style={{ background: '#fef0ef', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
              <div style={{ fontSize: '.72rem', color: '#8b1c1c', marginBottom: 8 }}>
                Permanently delete <strong>{form.name || 'this record'}</strong>? This cannot be undone.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleDelete} disabled={deleting}
                  style={{ padding: '6px 14px', background: '#e05c5c', color: '#fff', border: 'none', borderRadius: 6, fontSize: '.72rem', fontWeight: 600, cursor: 'pointer', fontFamily: F }}>
                  {deleting ? 'Deleting…' : 'Yes, delete'}
                </button>
                <button onClick={() => setConf(false)} style={{ padding: '6px 14px', background: '#f5fafc', color: '#6F8FA7', border: '1px solid #d8eaf4', borderRadius: 6, fontSize: '.72rem', cursor: 'pointer', fontFamily: F }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
              <button onClick={() => setConf(true)}
                style={{ padding: '7px 14px', background: '#fef0ef', color: '#c0392b', border: '1px solid #f5c6c6', borderRadius: 7, fontSize: '.7rem', fontWeight: 500, cursor: 'pointer', fontFamily: F }}>
                Delete record
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onClose} style={{ padding: '7px 14px', background: '#f5fafc', color: '#6F8FA7', border: '1px solid #d8eaf4', borderRadius: 7, fontSize: '.7rem', cursor: 'pointer', fontFamily: F }}>
                  Cancel
                </button>
                <button onClick={handleSave} disabled={saving || !dirty}
                  style={{ padding: '7px 18px', background: dirty ? '#314F5C' : '#c8d8e0', color: '#fff', border: 'none', borderRadius: 7, fontSize: '.72rem', fontWeight: 600, cursor: dirty ? 'pointer' : 'default', fontFamily: F }}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
